const HTTP_PROTOCOLS = new Set(['http:', 'https:'])
const RELATIVE_URL_BASE = new URL('https://relative-url.invalid')
const SAFE_IMAGE_DATA_URL_PATTERN =
  /^data:image\/(?:avif|gif|jpeg|png|webp);base64,[a-z0-9+/=\s]+$/i

const hasUrlControlCharacter = (value: string) =>
  Array.from(value).some((character) => {
    const characterCode = character.charCodeAt(0)
    return characterCode <= 0x1f || characterCode === 0x7f
  })

export const getSafeHttpUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }

  const candidate = value.trim()
  if (!candidate || hasUrlControlCharacter(candidate)) {
    return undefined
  }

  try {
    if (candidate.startsWith('/')) {
      const relativeUrl = new URL(candidate, RELATIVE_URL_BASE)
      if (
        relativeUrl.origin !== RELATIVE_URL_BASE.origin ||
        relativeUrl.pathname.startsWith('//')
      ) {
        return undefined
      }

      return `${relativeUrl.pathname}${relativeUrl.search}${relativeUrl.hash}`
    }

    const url = new URL(candidate)
    if (!HTTP_PROTOCOLS.has(url.protocol) || url.username || url.password) {
      return undefined
    }

    return url.toString()
  } catch {
    return undefined
  }
}

/**
 * Encodes a URL like encodeURI, but keeps existing valid escape
 * sequences (%XX) intact instead of double-encoding their percent sign.
 */
export const encodeUrlOnce = (url: string): string =>
  encodeURI(url).replace(/%25([0-9a-f]{2})/gi, '%$1')

export const getSafeImageUrl = (value: unknown): string | undefined => {
  const safeHttpUrl = getSafeHttpUrl(value)
  if (safeHttpUrl) {
    return safeHttpUrl
  }

  if (typeof value !== 'string') {
    return undefined
  }

  const candidate = value.trim()
  if (SAFE_IMAGE_DATA_URL_PATTERN.test(candidate)) {
    return candidate
  }

  try {
    const url = new URL(candidate)
    if (url.protocol === 'blob:' && HTTP_PROTOCOLS.has(new URL(url.origin).protocol)) {
      return url.toString()
    }
  } catch {
    return undefined
  }

  return undefined
}
